import { Button } from 'antd';
import { useCallback } from 'react';
import { ExpendableTable } from '../../../components/Table/expendableTable';
import { Options } from '../../../utils/Data';
import { safeArray } from '../utils/helpers';
import { medicineInit } from '../utils/constants';

export const MedicinesSection = ({
    values,
    errors,
    touched,
    allDates,
    todayDate,
    setFieldValue,
    setFieldTouched,
    handleSubmit,
    loading,
    setError,
}) => {
    // Get the next available date based on current medicines
    const getNextAvailableDate = useCallback((currentMedicines) => {
        const defaultDate = allDates && allDates.length > 0 ? allDates[0] : todayDate;
        if (!allDates || allDates.length === 0) return defaultDate;

        // Get all dates that are already used
        const usedDates = currentMedicines.map(med => med.date);

        // Find the first date that's not used
        const nextDate = allDates.find(date => !usedDates.includes(date));

        // If all dates are used, find the date after the last medicine's date
        if (!nextDate && currentMedicines.length > 0) {
            const lastMedicineDate = currentMedicines[currentMedicines.length - 1].date;
            const lastDateIndex = allDates.indexOf(lastMedicineDate);

            // Return next date in sequence if available
            if (lastDateIndex >= 0 && lastDateIndex < allDates.length - 1) {
                return allDates[lastDateIndex + 1];
            }
        }

        return nextDate || defaultDate;
    }, [allDates, todayDate]);

    // Smart onChange handler that detects new row addition and sorts by date ascending
    const handleMedicineChange = useCallback((newMedicines) => {
        try {
            const currentMedicines = values?.medicines || [];
            let updatedMedicines = [...newMedicines];

            // If a row was deleted, set the remaining rows to Formik immediately and exit
            if (updatedMedicines.length < currentMedicines.length) {
                setFieldValue('medicines', updatedMedicines, true);
                return;
            }

            // Check if a new row was added (length increased by 1)
            if (updatedMedicines.length === currentMedicines.length + 1) {
                const lastIndex = updatedMedicines.length - 1;
                const newRow = updatedMedicines[lastIndex];

                // Check if it's a newly added empty row (all values match medicineInit or the defaultDate)
                const isNewEmptyRow = Object.keys(medicineInit).every(key => {
                    const val = newRow[key];
                    const initVal = medicineInit[key];
                    const defaultDate = allDates && allDates.length > 0 ? allDates[0] : "";
                    if (key === 'date') {
                        return val === initVal || val === defaultDate;
                    }
                    if (Array.isArray(val) && Array.isArray(initVal)) {
                        return val.length === 0 && initVal.length === 0;
                    }
                    return val === initVal;
                });

                if (isNewEmptyRow && currentMedicines.length > 0) {
                    // Copy values from the previous row
                    const previousRow = currentMedicines[currentMedicines.length - 1];
                    const nextDate = getNextAvailableDate(currentMedicines);

                    // Create new row with copied values and next date, keeping the new medicine record clean of old _id if existing
                    const { _id, ...copiedRowWithoutId } = previousRow;
                    updatedMedicines[lastIndex] = {
                        ...medicineInit,
                        ...copiedRowWithoutId,
                        date: nextDate,
                    };
                }
            }

            // Sort medicines in ascending date order using allDates as the canonical sequence.
            // Rows without a date are placed at the end. Response structure is unaffected
            // since transformMedicinesData groups by date key regardless of array order.
            const sortedMedicines = updatedMedicines.sort((a, b) => {
                const dateList = allDates || [];
                const idxA = a.date ? dateList.indexOf(a.date) : -1;
                const idxB = b.date ? dateList.indexOf(b.date) : -1;
                // Fall back to lexicographic comparison for dates not in allDates
                if (idxA === -1 && idxB === -1) return (a.date || '').localeCompare(b.date || '');
                if (idxA === -1) return 1;
                if (idxB === -1) return -1;
                return idxA - idxB;
            });

            setFieldValue('medicines', sortedMedicines, true);
        } catch (err) {
            console.error('Error updating medicines:', err);
            setError?.('Failed to update medicines');
        }
    }, [values?.medicines, getNextAvailableDate, setFieldValue, setError, allDates]);

    return (
        <div className="card p-3">
            <ExpendableTable
                Title="Medicines"
                emptyDataForCreatingField={{
                    ...medicineInit,
                    date: allDates && allDates.length > 0 ? allDates[0] : ""
                }}
                Data={values?.medicines || []}
                maxRows={allDates?.length || null}
                Columns={[
                    { name: 'medicineName', type: 'medicinePicker', label: 'Medicine Name' },
                    {
                        name: 'whenToTake',
                        type: 'multiple',
                        label: 'When to Take',
                        options: ['On Hold', 'Not Taken', ...safeArray(Options?.WhenToTakeMedicine)],
                    },
                    { name: 'howMuch', type: 'number', label: 'Quantity' },
                    {
                        name: 'course',
                        type: 'singleSelect',
                        label: 'Course Duration',
                        options: safeArray(Options?.CourseDuration),
                    },
                    {
                        name: 'anupan',
                        type: 'singleSelect',
                        label: 'Anupan',
                        options: safeArray(Options?.Anupana),
                    },
                    { name: 'days', type: 'number', label: 'Days' },
                    {
                        name: 'date',
                        type: 'singleSelect',
                        label: 'Date',
                        options: safeArray(allDates),
                    },
                    { name: 'Action', type: 'action', label: 'Action' },
                ]}
                onChange={handleMedicineChange}
                onBlur={v => {
                    try {
                        setFieldTouched('medicines', v, true);
                    } catch (err) {
                        console.error('Error touching medicines field:', err);
                    }
                }}
                errors={errors?.medicines}
                touched={touched?.medicines}
            />

            <div className="d-flex justify-content-end mb-4 mt-3">
                <Button
                    type="primary"
                    className="text-left"
                    onClick={() => {
                        try {
                            handleSubmit();
                        } catch (err) {
                            console.error('Error in save button:', err);
                            setError?.('Failed to save plan');
                        }
                    }}
                    disabled={loading}
                >
                    {loading ? 'Saving...' : 'Save Plan'}
                </Button>
            </div>
        </div>
    );
};
